import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Link,
    useTheme,
    alpha
} from '@mui/material';
import { authService } from '../../services/authService';
import { extractApiErrorMessage } from '../../utils/apiUtils';
import { DEFAULT_THEME } from '../../constants/themeDefaults';
import LoginLayout from '../../components/layout/LoginLayout';
import LockResetIcon from '@mui/icons-material/LockReset';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

export const ResetPassword: React.FC = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [isValidToken, setIsValidToken] = useState(true);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = DEFAULT_THEME;

    const token = searchParams.get('token');
    const email = searchParams.get('email');

    useEffect(() => {
        if (!token || !email) {
            setIsValidToken(false);
            setMessage({ type: 'error', text: 'Invalid or missing reset link. Please request a new password reset.' });
        }
    }, [token, email]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            setMessage({ type: 'error', text: 'Passwords do not match.' });
            return;
        }

        if (!token || !email) {
            setMessage({ type: 'error', text: 'Invalid reset link.' });
            return;
        }

        setIsLoading(true);
        setMessage(null);

        try {
            const result = await authService.resetPassword(email, token, newPassword);
            
            if (result.success) {
                setMessage({ type: 'success', text: 'Password reset successfully! You can now log in with your new password.' });
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setMessage({ type: 'error', text: result.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: extractApiErrorMessage(error, 'An unexpected error occurred. Please try again.') });
        } finally {
            setIsLoading(false);
        }
    };

    if (!isValidToken) {
        return (
            <LoginLayout>
                <Container component="main" maxWidth="sm">
                    <Box
                        sx={{
                            marginTop: 8,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <Paper
                            elevation={3}
                            sx={{
                                padding: 4,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                backgroundColor: alpha(theme.backgroundColor, 0.9),
                                backdropFilter: 'blur(10px)',
                                borderRadius: 3,
                                border: `1px solid ${alpha(theme.primaryColor, 0.2)}`,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <CalendarMonthIcon
                                    sx={{
                                        fontSize: 40,
                                        color: theme.primaryColor,
                                        mr: 1,
                                    }}
                                />
                                <Typography
                                    component="h1"
                                    variant="h4"
                                    sx={{
                                        color: theme.textColor,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    MindShare
                                </Typography>
                            </Box>

                            <Typography
                                component="h2"
                                variant="h5"
                                sx={{
                                    color: theme.textColor,
                                    mb: 3,
                                    textAlign: 'center',
                                }}
                            >
                                Invalid Reset Link
                            </Typography>

                            {message && (
                                <Alert
                                    severity={message.type}
                                    sx={{ mb: 2, width: '100%', whiteSpace: 'pre-line' }}
                                >
                                    {message.text}
                                </Alert>
                            )}

                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Link
                                    component={RouterLink}
                                    to="/forgot-password"
                                    variant="body2"
                                    sx={{
                                        color: theme.primaryColor,
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    Request New Password Reset
                                </Link>
                            </Box>
                        </Paper>
                    </Box>
                </Container>
            </LoginLayout>
        );
    }

    return (
        <LoginLayout>
            <Container component="main" maxWidth="sm">
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            padding: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: alpha(theme.backgroundColor, 0.9),
                            backdropFilter: 'blur(10px)',
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.primaryColor, 0.2)}`,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <CalendarMonthIcon
                                sx={{
                                    fontSize: 40,
                                    color: theme.primaryColor,
                                    mr: 1,
                                }}
                            />
                            <Typography
                                component="h1"
                                variant="h4"
                                sx={{
                                    color: theme.textColor,
                                    fontWeight: 'bold',
                                }}
                            >
                                MindShare
                            </Typography>
                        </Box>

                        <LockResetIcon
                            sx={{
                                fontSize: 48,
                                color: theme.primaryColor,
                                mb: 2,
                            }}
                        />

                        <Typography
                            component="h2"
                            variant="h5"
                            sx={{
                                color: theme.textColor,
                                mb: 3,
                                textAlign: 'center',
                            }}
                        >
                            Reset Your Password
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                color: theme.textColor,
                                mb: 3,
                                textAlign: 'center',
                                opacity: 0.8,
                            }}
                        >
                            Enter your new password below.
                        </Typography>

                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                            {message && (
                                <Alert
                                    severity={message.type}
                                    sx={{ mb: 2, whiteSpace: 'pre-line' }}
                                >
                                    {message.text}
                                </Alert>
                            )}

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="newPassword"
                                label="New Password"
                                type="password"
                                id="newPassword"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: alpha(theme.primaryColor, 0.3),
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.primaryColor,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.primaryColor,
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.textColor,
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: theme.primaryColor,
                                    },
                                }}
                            />

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="confirmPassword"
                                label="Confirm New Password"
                                type="password"
                                id="confirmPassword"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: alpha(theme.primaryColor, 0.3),
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.primaryColor,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.primaryColor,
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.textColor,
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: theme.primaryColor,
                                    },
                                }}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                sx={{
                                    mt: 3,
                                    mb: 2,
                                    backgroundColor: theme.primaryColor,
                                    '&:hover': {
                                        backgroundColor: alpha(theme.primaryColor, 0.8),
                                    },
                                    '&:disabled': {
                                        backgroundColor: alpha(theme.primaryColor, 0.5),
                                    },
                                }}
                            >
                                {isLoading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    'Reset Password'
                                )}
                            </Button>

                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Link
                                    component={RouterLink}
                                    to="/login"
                                    variant="body2"
                                    sx={{
                                        color: theme.primaryColor,
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    Back to Login
                                </Link>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Container>
        </LoginLayout>
    );
};

export default ResetPassword;
